/**
 * @file Reads a configuration value for the environment the app is running in.
 */
import { STAGING_HOSTS, PRODUCTION_DOMAINS, CONFIG } from "../../config.js";

export const PRD_ENV = "prd";
export const DEV_ENV = "dev";
const STG_ENV = "stg";

export type Environment = typeof PRD_ENV | typeof DEV_ENV | typeof STG_ENV;

/**
 * Detects the environment the app is running in, from the hostname of the page.
 */
export const getEnvironment = (): Environment => {
  const { hostname } = window.location;

  const isStaging = STAGING_HOSTS.includes(hostname);

  if (isStaging) {
    return STG_ENV;
  }

  const productionDomains = PRODUCTION_DOMAINS
    .filter((domain) => hostname.includes(domain));

  const isProduction = productionDomains.length > 0;

  if (isProduction) {
    return PRD_ENV;
  }

  return DEV_ENV;
};

/**
 * Reads the named value from the configuration of the current environment, and throws
 * when the environment defines no value for it.
 */
const getConfig = <T = unknown>(name: string): T => {
  const environment = getEnvironment();
  const environmentConfig: Record<string, unknown> = CONFIG[environment];
  const value = environmentConfig[name];

  const isUndefined = value === undefined;

  if (isUndefined) {
    throw new Error(`Configuration variable ${name} is undefined in ${environment} config`);
  }

  return value as T;
};

export default getConfig;
